<script setup>
// ---------------------------------------------------------------------------
// Contract size against how long the case took.
//
// The prototype puts these on one plot with two y axes — counts as bars, mean
// duration as a line across them. Two y axes are the most common and the most
// damaging mistake in charting: the reader sees the line cross the bars and
// reads meaning into the crossings, which is an artefact of the two scales.
//
// Here the three measures keep their own columns and share only the rows, which
// are the price bands. Nothing crosses anything.
//
// The middle column is the point of the whole chart: the share of that band's
// market the Office reviewed. It runs from half a percent to nearly a fifth,
// which is the sentence "the bigger the purchase, the likelier the review".
// ---------------------------------------------------------------------------

import { computed } from 'vue'

import { bandShare, narrowContracts, outsideFilters, sizeBands, visibleContracts } from '../cases.js'
import { filters } from '../store.js'
import { useContracts, useJson } from '../useCases.js'

// The longest bar stops short of the full width so its own figure still fits
// beside it. The duration scale is relative anyway — nothing is lost by it.
const SPAN = 82

const props = defineProps({
    src: { type: String, required: true },
    bandLabels: { type: String, default: '' },   // pipe-separated, low to high
    marketSrc: { type: String, required: true },
    headCount: { type: String, default: '' },
    headShare: { type: String, default: '' },
    headMedian: { type: String, default: '' },
    unitDays: { type: String, default: '' },
    note: { type: String, default: '' },
    errorText: { type: String, default: '' }
})

const { rows, visible, failed } = useContracts(props.src)
const { data: table } = useJson(props.marketSrc, { rows: [] })

// The middle column is a share of the market, so it counts every contract of
// that size the Office saw — orders and court cases included, because the market
// side cannot tell those apart. The other two columns stay on `visible`.
const shares = computed(() =>
    bandShare(narrowContracts(rows.value, outsideFilters(filters)), table.value.rows, filters)
)

const bands = computed(() => {
    if (!rows.value.length) return null

    const labels = props.bandLabels.split('|')
    return sizeBands(visible.value).map((band, index) => ({ ...band, label: labels[index] ?? '' }))
})

// Only contracts whose case has a measurable length are drawn. Counting all the
// priced ones on the left and timing a subset of them on the right put two
// different populations side by side and invited the reader to read one as the
// other.
//
// Counts are parts of one whole, so their track is that whole and the four bars
// add up to it. Durations are not: adding four medians together means nothing,
// so their bars share the largest as a scale and get no track — a full-width
// track is a promise about a total, and here there is none.
const measured = computed(() => bands.value.reduce((total, band) => total + band.counted, 0) || 1)

const longest = computed(() => Math.max(...bands.value.map((band) => band.median), 1))

const likeliest = computed(() => Math.max(...shares.value.map((band) => band.share), 0.0001))

const priced = computed(() => bands.value.reduce((sum, band) => sum + band.contracts, 0))

const number = (value) => value.toLocaleString('cs-CZ', { maximumFractionDigits: 1 })

const percent = (value) =>
    value.toLocaleString('cs-CZ', { style: 'percent', minimumFractionDigits: 1, maximumFractionDigits: 1 })

// How many contracts in each band actually have a measurable duration. The two
// columns are counted on different bases and the reader cannot see that.
const filled = computed(() =>
    props.note.replace('{merene}', number(measured.value)).replace('{vsechny}', number(priced.value))
)
</script>

<template>
    <p v-if="failed" class="chart__failed">{{ errorText }}</p>

    <div v-else-if="bands" class="chart">
        <div class="panels">
            <div class="panels__head">
                <span></span>
                <span class="panels__title">{{ headCount }}</span>
                <span class="panels__title">{{ headShare }}</span>
                <span class="panels__title">{{ headMedian }}</span>
            </div>

            <div v-for="(band, index) in bands" :key="band.label" class="panels__row">
                <span class="bars__label">{{ band.label }}</span>

                <!-- Each cell repeats its column heading. On a narrow screen the
                     head row is gone and three unlabelled bars under one price
                     band say nothing about which measure is which. -->
                <span class="panels__cell">
                    <span class="panels__label">{{ headCount }}</span>
                    <span class="bars__track">
                        <span
                            class="bars__fill"
                            :style="{ width: `${(band.counted / measured) * 100}%` }"
                        ></span>
                        <span class="bars__value">{{ number(band.counted) }}</span>
                    </span>
                </span>

                <span class="panels__cell">
                    <span class="panels__label">{{ headShare }}</span>
                    <span class="bars__track bars__track--open">
                        <span
                            class="bars__fill bars__fill--third"
                            :style="{ width: `${(shares[index].share / likeliest) * SPAN}%` }"
                        ></span>
                        <span class="bars__value">{{ percent(shares[index].share) }}</span>
                    </span>
                </span>

                <span class="panels__cell">
                    <span class="panels__label">{{ headMedian }}</span>
                    <span class="bars__track bars__track--open">
                        <span
                            class="bars__fill bars__fill--second"
                            :style="{ width: `${(band.median / longest) * SPAN}%` }"
                        ></span>
                        <span class="bars__value">{{ number(band.median) }} {{ unitDays }}</span>
                    </span>
                </span>
            </div>
        </div>

        <p class="note">{{ filled }}</p>
    </div>
</template>
